/**
 * atomberg-aes-token.js
 *
 * Request tokens for Atomberg services that authenticate with a shared AES-256
 * key: base64(iv + AES-256-CBC(plaintext)), with a fresh random IV per call.
 * Resolves null when the key is malformed or crypto.subtle is missing (it only
 * exists in secure contexts), so callers can skip the request.
 *
 * Ported from waterApi.ts in the atomberg-water-quality repo — that bundle is
 * built as a bare IIFE and exports nothing, so it cannot be shared.
 *
 * API (window.AtombergAesToken):
 *   encrypt(secretHex, plaintext) → Promise<string|null>
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w.AtombergAesToken) return;

  /** @param {string} hex */
  function hexToBytes(hex) {
    // Backed by an explicit ArrayBuffer so the result satisfies BufferSource
    var arr = new Uint8Array(new ArrayBuffer(hex.length / 2));
    for (var i = 0; i < hex.length; i += 2) {
      arr[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }
    return arr;
  }

  /**
   * @param {string} secretHex - 64-char hex AES-256 key
   * @param {string} plaintext
   * @returns {Promise<string|null>}
   */
  function encrypt(secretHex, plaintext) {
    if (!/^[0-9a-f]{64}$/i.test(secretHex)) return Promise.resolve(null);
    if (typeof crypto === 'undefined' || !crypto.subtle) return Promise.resolve(null);

    var iv = crypto.getRandomValues(new Uint8Array(16));
    var payload = new TextEncoder().encode(plaintext);

    return crypto.subtle
      .importKey('raw', hexToBytes(secretHex), { name: 'AES-CBC' }, false, ['encrypt'])
      .then(function (key) {
        return crypto.subtle.encrypt({ name: 'AES-CBC', iv: iv }, key, payload);
      })
      .then(function (cipher) {
        var combined = new Uint8Array(16 + cipher.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(cipher), 16);
        var binary = '';
        for (var i = 0; i < combined.length; i++) {
          binary += String.fromCharCode(combined[i]);
        }
        return btoa(binary);
      })
      .catch(function () {
        return null;
      });
  }

  w.AtombergAesToken = { encrypt: encrypt };
})();
